import React from 'react';
import { format, isSameMonth, isToday } from 'date-fns';
import { nb } from 'date-fns/locale';
import { Lock, Plus } from 'lucide-react';
import { COURTS, HOURS, CLOSE_HOUR, pad, toKey, weekDays, monthGrid, bookingAt, isBlock, hourOf } from './tennisCalendarUtils';

const cellBase = 'rounded-md text-xs leading-tight px-2 py-1.5 w-full text-left transition-colors';

const SlotCell = ({ booking, onFree, onBooking, compact, freeId, isStart = true, tall }) => {
  if (!booking) {
    return (
      <button type="button" onClick={onFree} data-testid={freeId} className={`${cellBase} min-h-[38px] bg-slate-50 hover:bg-[#CCFF00]/40 border border-dashed border-slate-200 text-slate-400 group`} aria-label="Ledig – klikk for å booke">
        <span className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100"><Plus className="w-3 h-3" />{!compact && 'Ledig'}</span>
      </button>
    );
  }
  const block = isBlock(booking);
  const end = pad(hourOf(booking.time_slot) + (booking.duration_hours || 1));
  return (
    <button
      type="button"
      onClick={() => onBooking(booking)}
      style={tall ? { minHeight: `${tall * 38 + (tall - 1) * 4}px` } : undefined}
      className={`${cellBase} h-full min-h-[38px] border ${block ? 'bg-slate-200 border-slate-300 text-slate-700 bg-[repeating-linear-gradient(45deg,transparent,transparent_6px,rgba(0,0,0,0.05)_6px,rgba(0,0,0,0.05)_12px)]' : 'bg-green-500/15 border-green-500/40 text-slate-900 hover:bg-green-500/25'} ${!isStart ? 'opacity-70' : ''}`}
      data-testid={isStart ? `cal-booking-${booking.id}` : undefined}
      aria-label={`${booking.member_name} ${booking.time_slot}–${end}`}
    >
      {isStart ? (
        <>
          <span className="flex items-center gap-1 font-semibold truncate">
            {block && <Lock className="w-3 h-3 flex-shrink-0" />}
            <span className="truncate">{booking.member_name}</span>
          </span>
          {!compact && <span className="block text-[10px] text-slate-500">{booking.time_slot}–{end}</span>}
        </>
      ) : (
        <span className="block text-[10px] text-slate-500 text-center">↓</span>
      )}
    </button>
  );
};

export const DayView = ({ date, bookings, courtFilter, onFree, onBooking }) => {
  const key = toKey(date);
  const courts = COURTS.filter((c) => !courtFilter || c === courtFilter);
  return (
    <div className="overflow-x-auto" data-testid="tennis-cal-day">
      <table className="w-full table-fixed border-separate border-spacing-1 min-w-[420px]">
        <colgroup>
          <col className="w-16" />
          {courts.map((c) => <col key={c} />)}
        </colgroup>
        <thead>
          <tr>
            <th className="text-left text-xs text-slate-500 font-medium px-2">Tid</th>
            {courts.map((c) => <th key={c} className="text-left text-xs text-slate-500 font-medium px-2">Bane {c}</th>)}
          </tr>
        </thead>
        <tbody>
          {HOURS.map((h) => (
            <tr key={h}>
              <td className="text-xs text-slate-600 font-mono px-2 align-top pt-2 h-[42px]">{pad(h)}</td>
              {courts.map((c) => {
                const b = bookingAt(bookings, key, c, h);
                if (b && hourOf(b.time_slot) !== h) return null;
                const span = b ? Math.min(b.duration_hours || 1, CLOSE_HOUR - h) : 1;
                return (
                  <td key={c} rowSpan={span} className="align-top h-[42px]">
                    <SlotCell freeId={`cal-free-${key}-${c}-${h}`} booking={b} tall={span > 1 ? span : undefined} onFree={() => onFree(key, c, h)} onBooking={onBooking} />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const WeekView = ({ date, bookings, courtFilter, onFree, onBooking, onPickDay }) => {
  const days = weekDays(date);
  const courts = COURTS.filter((c) => !courtFilter || c === courtFilter);
  return (
    <div className="overflow-x-auto" data-testid="tennis-cal-week">
      <table className="w-full table-fixed border-separate border-spacing-1 min-w-[900px]">
        <colgroup>
          <col className="w-14" />
          {days.map((d) => <col key={toKey(d)} />)}
        </colgroup>
        <thead>
          <tr>
            <th className="w-14 text-left text-xs text-slate-500 font-medium px-1">Tid</th>
            {days.map((d) => (
              <th key={toKey(d)} className="px-1">
                <button type="button" onClick={() => onPickDay(d)} className={`w-full text-center rounded-md py-1 text-xs font-semibold ${isToday(d) ? 'bg-[#CCFF00] text-black' : 'text-slate-700 hover:bg-slate-100'}`}>
                  {format(d, 'EEE d.M', { locale: nb })}
                </button>
                <div className="grid grid-cols-2 gap-1 mt-1 text-[10px] text-slate-400">{courts.map((c) => <span key={c}>B{c}</span>)}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {HOURS.map((h) => (
            <tr key={h}>
              <td className="text-xs text-slate-600 font-mono px-1 align-top pt-2">{pad(h)}</td>
              {days.map((d) => {
                const key = toKey(d);
                return (
                  <td key={key} className="align-top">
                    <div className={`grid gap-1 ${courts.length === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                      {courts.map((c) => {
                        const b = bookingAt(bookings, key, c, h);
                        return <SlotCell key={c} compact freeId={`cal-free-${key}-${c}-${h}`} booking={b} isStart={!b || hourOf(b.time_slot) === h} onFree={() => onFree(key, c, h)} onBooking={onBooking} />;
                      })}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const MonthView = ({ date, bookings, onPickDay }) => {
  const days = monthGrid(date);
  const active = bookings.filter((b) => b.status !== 'cancelled');
  return (
    <div data-testid="tennis-cal-month">
      <div className="grid grid-cols-7 gap-1 mb-1">
        {['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'].map((d) => <div key={d} className="text-center text-xs text-slate-500 font-medium py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d) => {
          const key = toKey(d);
          const dayBookings = active.filter((b) => b.date === key);
          const blocks = dayBookings.filter(isBlock).length;
          const regular = dayBookings.length - blocks;
          const hours = dayBookings.reduce((s, b) => s + (b.duration_hours || 1), 0);
          return (
            <button
              key={key}
              type="button"
              onClick={() => onPickDay(d)}
              data-testid={`cal-month-day-${key}`}
              className={`min-h-[64px] sm:min-h-[84px] rounded-lg border p-1.5 text-left flex flex-col transition-colors ${isSameMonth(d, date) ? 'bg-white border-slate-200 hover:border-[#CCFF00]' : 'bg-slate-50 border-slate-100 text-slate-400'}`}
            >
              <span className={`text-xs font-semibold self-end rounded-full w-6 h-6 flex items-center justify-center ${isToday(d) ? 'bg-[#CCFF00] text-black' : ''}`}>{format(d, 'd')}</span>
              {regular > 0 && <span className="mt-auto text-[10px] sm:text-xs bg-green-500/15 text-green-700 rounded px-1 truncate">{regular} booking{regular > 1 ? 'er' : ''}</span>}
              {blocks > 0 && <span className="mt-0.5 text-[10px] sm:text-xs bg-slate-200 text-slate-600 rounded px-1 truncate flex items-center gap-1"><Lock className="w-2.5 h-2.5" />{blocks} blokk</span>}
              {hours > 0 && <span className="hidden sm:block text-[10px] text-slate-400 mt-0.5">{hours} t opptatt</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};
